import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import DerivativeChip from "./DerivativeChip";
import { RELATION_ORDER, type RelationType } from "@/lib/philology/relationTypes";
import type { DerivativeRecord } from "@/lib/philology/api";

interface WordFamilyAccordionProps {
  derivatives: DerivativeRecord[];
  activeWord?: string | null;
  onSelect?: (d: DerivativeRecord) => void;
}

export default function WordFamilyAccordion({ derivatives, activeWord, onSelect }: WordFamilyAccordionProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByRelation(derivatives), [derivatives]);
  const firstNonEmpty = useMemo(() => {
    for (const rel of RELATION_ORDER) {
      if ((groups[rel]?.length ?? 0) > 0) return rel;
    }
    return null;
  }, [groups]);
  const [openSet, setOpenSet] = useState<Set<RelationType>>(() => new Set(firstNonEmpty ? [firstNonEmpty] : []));

  function toggle(rel: RelationType) {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(rel)) next.delete(rel);
      else next.add(rel);
      return next;
    });
  }

  if (derivatives.length === 0) {
    return (
      <div className="text-center py-10 text-[hsl(var(--muted-foreground))]">
        <p className="text-sm">{t("philology.empty")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {RELATION_ORDER.map((rel) => {
        const list = groups[rel];
        if (!list || list.length === 0) return null;
        const isOpen = openSet.has(rel);
        return (
          <section key={rel} className="rounded-2xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] overflow-hidden shadow-sm">
            <button
              type="button"
              onClick={() => toggle(rel)}
              className="w-full px-4 py-3 flex items-center justify-between gap-2 text-left hover:bg-[hsl(var(--bg3))]/40 transition-colors"
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">
                  {t(`philology.relationTypes.${rel}`)}
                </span>
                <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-md bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))]">{list.length}</span>
              </div>
              <ChevronDown size={18} className={`shrink-0 text-[hsl(var(--muted-foreground))] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-3 pb-3 pt-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {list.map((d, i) => (
                  <DerivativeChip
                    key={`${rel}-${d.word}-${i}`}
                    derivative={d}
                    isActive={activeWord != null && d.word === activeWord}
                    onClick={onSelect}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function groupByRelation(list: DerivativeRecord[]): Partial<Record<RelationType, DerivativeRecord[]>> {
  const out: Partial<Record<RelationType, DerivativeRecord[]>> = {};
  for (const d of list) {
    const key = d.relationType;
    if (!out[key]) out[key] = [];
    out[key]!.push(d);
  }
  return out;
}
